"use client";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ReactNode } from "react";
import { DraggableRowItem } from "./draggable-row-item";
import { cn } from "@drpcrd/utils/shared";

export type DraggableListItem = {
  id: string;
  leftElement: ReactNode;
  title: ReactNode;
  rightElement?: ReactNode;
  isDraggable?: boolean;
};

type Props = {
  items: DraggableListItem[];
  onReorder: (items: DraggableListItem[]) => void;
  onDelete?: (id: string) => void;
  className?: string;
};

/**
 * Parent component that handles a list of draggable row items with reordering functionality.
 */
export const DraggableList = ({
  items,
  onReorder,
  onDelete,
  className,
}: Props) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorder(newItems);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className={cn("flex flex-col gap-1", className)}>
          {items.map((item) => (
            <DraggableRowItem
              key={item.id}
              id={item.id}
              leftElement={item.leftElement}
              title={item.title}
              rightElement={item.rightElement}
              isDraggable={item.isDraggable ?? true}
              onDelete={onDelete ? () => onDelete(item.id) : undefined}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
